/*
 * 模块: MetricsGuide 主页面
 * 职责: 页面布局、导航、搜索和指标分类展示
 * 依赖: antd组件、hooks、子组件、工具函数
 * 导出: MetricsGuide组件
 * 限制: 文件<280行, 函数<30行
 */

import React, { useState } from 'react';
import {
  Card,
  Alert,
  Anchor,
  Input,
  Row,
  Col,
  Typography,
  Skeleton,
} from 'antd';
import {
  SearchOutlined,
  BookOutlined,
  ThunderboltOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import { PageHeader } from '../../components/Layout/PageHeader';
import { useMetrics } from './hooks/useMetrics';
import { MetricCard } from './components/MetricCard';
import './styles/index.css';

const { Title, Text } = Typography;

const MetricsGuide: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const { filterMetrics, loading } = useMetrics(searchTerm);

  const coreMetrics = filterMetrics('core', searchTerm);
  const brandMetrics = filterMetrics('brand', searchTerm);
  const advancedMetrics = filterMetrics('advanced', searchTerm);

  const buildAnchorItems = () => {
    return [
      { key: 'core-metrics', href: '#core-metrics', title: '核心指标' },
      ...coreMetrics.map(([key, def]) => ({ key, href: `#${key}`, title: def.name })),
      { key: 'brand-metrics', href: '#brand-metrics', title: '品牌指标' },
      ...brandMetrics.map(([key, def]) => ({ key, href: `#${key}`, title: def.name })),
      { key: 'advanced-metrics', href: '#advanced-metrics', title: '高级指标' },
      ...advancedMetrics.map(([key, def]) => ({ key, href: `#${key}`, title: def.name })),
    ];
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <PageHeader
        title="指标说明"
        subtitle="深入了解每个指标的定义、计算方法、专业术语和实际应用"
        icon={<BookOutlined />}
        breadcrumbs={[
          { title: '首页', href: '/' },
          { title: '指标说明' },
        ]}
      />

      {/* 内容区域 - 可滚动 */}
      <div style={{ flex: 1, overflow: 'auto', padding: '24px', background: '#f5f5f5' }}>
        {/* 搜索栏 */}
        <Card style={{ marginBottom: 24 }}>
          <Input
            placeholder="搜索指标名称、描述或专业术语..."
            prefix={<SearchOutlined style={{ color: '#1890ff' }} />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="large"
            allowClear
          />
        </Card>

        {/* 主要内容区域 */}
        <div style={{ display: 'flex', gap: 24 }}>
          {/* 左侧导航 */}
          <div style={{ width: 240, flexShrink: 0 }}>
            <Card style={{ position: 'sticky', top: 24 }}>
              <Anchor 
                offsetTop={24}
                items={buildAnchorItems()}
              />
            </Card>
          </div>

          {/* 右侧内容 */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* 快速统计卡片 */}
            {loading ? (
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={8}>
                  <Card><Skeleton active paragraph={{ rows: 1 }} /></Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card><Skeleton active paragraph={{ rows: 1 }} /></Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card><Skeleton active paragraph={{ rows: 1 }} /></Card>
                </Col>
              </Row>
            ) : (
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={8}>
                  <Card className="stat-card core-stat">
                    <div className="stat-icon">
                      <ThunderboltOutlined />
                    </div>
                    <div className="stat-content">
                      <Text className="stat-label">核心指标</Text>
                      <Title level={2} className="stat-value">{coreMetrics.length}</Title>
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card className="stat-card brand-stat">
                    <div className="stat-icon">
                      <BookOutlined />
                    </div>
                    <div className="stat-content">
                      <Text className="stat-label">品牌指标</Text>
                      <Title level={2} className="stat-value">{brandMetrics.length}</Title>
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card className="stat-card advanced-stat">
                    <div className="stat-icon">
                      <RocketOutlined />
                    </div>
                    <div className="stat-content">
                      <Text className="stat-label">高级指标</Text>
                      <Title level={2} className="stat-value">{advancedMetrics.length}</Title>
                    </div>
                  </Card>
                </Col>
              </Row>
            )}
            {/* 核心指标 */}
            <section id="core-metrics" className="metrics-section">
              <div className="section-header">
                <div className="section-title-wrapper">
                  <ThunderboltOutlined className="section-icon" />
                  <Title level={2}>核心指标</Title>
                </div>
                <Text className="section-description">
                  核心指标是分析市场竞争格局的基础指标，反映市场的基本特征和竞争状态。这些指标直接影响市场机会评估和决策制定。
                </Text>
              </div>
              {loading ? (
                <>
                  <Card style={{ marginBottom: 16 }}><Skeleton active paragraph={{ rows: 4 }} /></Card>
                  <Card style={{ marginBottom: 16 }}><Skeleton active paragraph={{ rows: 4 }} /></Card>
                  <Card style={{ marginBottom: 16 }}><Skeleton active paragraph={{ rows: 4 }} /></Card>
                </>
              ) : coreMetrics.length > 0 ? (
                coreMetrics.map(([key, def]) => (
                  <MetricCard key={key} metricKey={key} definition={def} />
                ))
              ) : (
                <Alert message="没有找到匹配的核心指标" type="info" showIcon />
              )}
            </section>

            {/* 品牌指标 */}
            <section id="brand-metrics" className="metrics-section">
              <div className="section-header">
                <div className="section-title-wrapper">
                  <BookOutlined className="section-icon" />
                  <Title level={2}>品牌指标</Title>
                </div>
                <Text className="section-description">
                  品牌指标用于分析品牌在市场中的表现和影响力，帮助评估品牌竞争力和市场定位。包括基于经济学理论的HHI指数和信息论的熵指数。
                </Text>
              </div>
              {loading ? (
                <>
                  <Card style={{ marginBottom: 16 }}><Skeleton active paragraph={{ rows: 4 }} /></Card>
                  <Card style={{ marginBottom: 16 }}><Skeleton active paragraph={{ rows: 4 }} /></Card>
                </>
              ) : brandMetrics.length > 0 ? (
                brandMetrics.map(([key, def]) => (
                  <MetricCard key={key} metricKey={key} definition={def} />
                ))
              ) : (
                <Alert message="没有找到匹配的品牌指标" type="info" showIcon />
              )}
            </section>

            {/* 高级指标 */}
            <section id="advanced-metrics" className="metrics-section">
              <div className="section-header">
                <div className="section-title-wrapper">
                  <RocketOutlined className="section-icon" />
                  <Title level={2}>高级指标</Title>
                </div>
                <Text className="section-description">
                  高级指标综合多个维度，提供更深入的市场洞察和决策支持。这些指标结合了多个基础指标，反映市场的复杂特征。
                </Text>
              </div>
              {loading ? (
                <>
                  <Card style={{ marginBottom: 16 }}><Skeleton active paragraph={{ rows: 4 }} /></Card>
                  <Card style={{ marginBottom: 16 }}><Skeleton active paragraph={{ rows: 4 }} /></Card>
                </>
              ) : advancedMetrics.length > 0 ? (
                advancedMetrics.map(([key, def]) => (
                  <MetricCard key={key} metricKey={key} definition={def} />
                ))
              ) : (
                <Alert message="没有找到匹配的高级指标" type="info" showIcon />
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export { MetricsGuide };
export default MetricsGuide;
